import React from 'react';
import { useSecureAdminAuth } from '../auth/useSecureAdminAuth';
import { AdminAuth } from '../auth';
import '../styles/admin/AdminDashboard.css';

/**
 * Protected Admin Route Component
 * Wraps admin pages with secure authentication and authorization
 */

const ProtectedAdminRoute = ({ children, requiredPermissions = [] }) => {
  const { 
    adminUser, 
    isAdminLoading, 
    adminError, 
    adminSignOut,
    timeUntilExpiry,
    isAdmin // Add isAdmin from backend verification
  } = useSecureAdminAuth();

  // Show loading state
  if (isAdminLoading) {
    return (
      <div className="admin-loading-container">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <h3>Verifying admin credentials...</h3>
          <p>Please wait while we validate your access.</p>
        </div>
      </div>
    );
  }

  // Show access denied error if not admin
  if (adminError || !isAdmin) {
    return (
      <div className="admin-access-denied">
        <div className="access-denied-content">
          <h2>🚫 Access Denied</h2>
          <p>{adminError || 'You do not have admin privileges.'}</p>
          <div className="access-denied-details">
            <h4>Admin Access Requirements:</h4>
            <ul>
              <li>✅ Email address must be pre-approved by IT department</li>
              <li>✅ Admin privileges assigned by system administrator</li>
              <li>✅ Current authentication with Google</li>
            </ul>
          </div>
          <p>You will be redirected to the home page in 3 seconds...</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="btn-primary"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Show authentication form if not logged in
  if (!adminUser) {
    return (
      <div className="admin-auth-container">
        <div className="admin-auth-header">
          <h2>🔐 Admin Access Required</h2>
          <p>Please sign in with your approved Google account to access admin features.</p>
        </div>
        <AdminAuth />
        <div className="admin-auth-footer">
          <h4>Security Notice:</h4>
          <ul>
            <li>Only pre-approved administrators can access admin features</li>
            <li>All admin actions are logged and monitored</li>
            <li>Sessions expire after 30 minutes of inactivity</li>
            <li>Contact IT if you need admin access</li>
          </ul>
        </div>
      </div>
    );
  }

  // Session expiry warning
  const showSessionWarning = timeUntilExpiry < 5 * 60 * 1000; // 5 minutes
  
  return (
    <div className="protected-admin-content">
      {/* Session warning banner */}
      {showSessionWarning && (
        <div className="session-warning-banner">
          <span>⚠️ Admin session expires in {Math.ceil(timeUntilExpiry / 60000)} minutes</span>
          <button onClick={() => window.location.reload()}>Extend Session</button>
        </div>
      )}
      
      {/* Clean admin header */}
      <div className="admin-top-bar">
        <div className="admin-left-section">
          <div className="admin-user-info">
            <div className="admin-avatar">
              <img src={adminUser.photoURL} alt="Admin" />
            </div>
            <div className="admin-details">
              <span className="admin-name">{adminUser.displayName}</span>
              <span className="admin-email">{adminUser.email}</span>
              <span className="admin-badge">Admin Access</span>
            </div>
          </div>
        </div>
        
        <div className="admin-center-section">
          {window.location.pathname !== '/admin' && (
            <a href="/admin" className="admin-dashboard-link">
              Dashboard
            </a>
          )}
        </div>
        
        <div className="admin-right-section">
          <button onClick={adminSignOut} className="admin-sign-out-btn">
            Sign Out
          </button>
        </div>
      </div>
      
      {/* Protected admin content */}
      <div className="admin-content">
        {children}
      </div>
    </div>
  );
};

export default ProtectedAdminRoute;
